import axios from "axios";
import authService from "./authService";
import { appendDateParams } from "../utils/dateParams";

// Canonical API base URL (production). Kept in code so requests work even if env-config.js fails to load.
const PRODUCTION_API_BASE_URL = "https://insights.hillscoupons.co.za/api";

const getEnvVar = (name, fallback) => {
    // Check runtime environment variables (injected by Docker)
    if (window._env_ && window._env_[name]) {
        return window._env_[name];
    }
    // Check build-time environment variables
    if (process.env[name]) {
        return process.env[name];
    }
    // Return fallback
    return fallback;
};

/** Ensures production host always uses the /api path base (matches deployed API). */
const normalizeApiBaseUrl = (url) => {
    const value = (url || "").trim().replace(/\/$/, "");
    if (!value) {
        return PRODUCTION_API_BASE_URL;
    }
    if (value === "https://insights.hillscoupons.co.za") {
        return PRODUCTION_API_BASE_URL;
    }
    return value;
};

export const apiBaseURL = normalizeApiBaseUrl(
    getEnvVar("REACT_APP_API_BASE_URL", PRODUCTION_API_BASE_URL),
);

/**
 * Read timeout env without treating 0 as "missing" (getEnvVar uses truthy checks on window._env_).
 * REACT_APP_API_TIMEOUT_MS: 0 = no axios timeout.
 */
const getTimeoutEnvRaw = () => {
    if (
        window._env_ &&
        Object.prototype.hasOwnProperty.call(
            window._env_,
            "REACT_APP_API_TIMEOUT_MS",
        )
    ) {
        const v = window._env_.REACT_APP_API_TIMEOUT_MS;
        if (v === undefined || v === null) {
            return "";
        }
        return String(v).trim();
    }
    const build = process.env.REACT_APP_API_TIMEOUT_MS;
    if (build !== undefined && build !== null && String(build).trim() !== "") {
        return String(build).trim();
    }
    return "";
};

/** Client-side request timeout (ms). 0 = no timeout. In development, defaults to 0 unless REACT_APP_API_TIMEOUT_MS is set. */
const parseApiTimeoutMs = () => {
    const raw = getTimeoutEnvRaw();
    if (raw === "") {
        return process.env.NODE_ENV === "development" ? 0 : 10000;
    }
    const trimmed = raw.toLowerCase();
    if (
        trimmed === "0" ||
        trimmed === "off" ||
        trimmed === "false" ||
        trimmed === "none"
    ) {
        return 0;
    }
    const n = Number(raw);
    if (!Number.isFinite(n) || n < 0) {
        return process.env.NODE_ENV === "development" ? 0 : 10000;
    }
    return Math.floor(n);
};

const API_TIMEOUT_MS = parseApiTimeoutMs();

// Create axios instance
const apiClient = axios.create({
    baseURL: apiBaseURL,
    timeout: API_TIMEOUT_MS,
    headers: {
        "Content-Type": "application/json",
    },
});

// Request interceptor to add auth token
apiClient.interceptors.request.use(
    async (config) => {
        try {
            // Check if user is authenticated before making request
            if (!authService.isAuthenticated()) {
                console.warn("User not authenticated, redirecting to login");
                authService.logout();
                return Promise.reject(new Error("User not authenticated"));
            }

            // Ensure token is valid (refresh if needed within 30 seconds of expiry)
            await authService.updateToken(30);

            // Add authorization header with Bearer token
            const authHeader = authService.getAuthorizationHeader();
            if (authHeader) {
                config.headers.Authorization = authHeader;
            } else {
                console.warn(
                    "No auth token available for request:",
                    config.url,
                );
                return Promise.reject(
                    new Error("No authentication token available"),
                );
            }

            return config;
        } catch (error) {
            console.error("Error in request interceptor:", error);
            // If token refresh fails, logout user
            if (
                error.message?.includes("Failed to refresh token") ||
                error.message?.includes("token")
            ) {
                console.warn("Token refresh failed, logging out user");
                authService.logout();
            }
            return Promise.reject(error);
        }
    },
    (error) => {
        console.error("Request interceptor error:", error);
        return Promise.reject(error);
    },
);

// Response interceptor to handle auth errors and token refresh
apiClient.interceptors.response.use(
    (response) => {
        return response;
    },
    async (error) => {
        const originalRequest = error.config;

        // Handle 401 Unauthorized responses
        if (error.response?.status === 401) {
            console.warn("Unauthorized request detected:", {
                url: error.config?.url,
                status: error.response.status,
            });

            // Check if this is the first retry attempt
            if (!originalRequest._retry) {
                originalRequest._retry = true;

                try {
                    // Try to refresh the token
                    await authService.updateToken(0); // Force refresh

                    // Update the authorization header for retry
                    const newAuthHeader = authService.getAuthorizationHeader();
                    if (newAuthHeader) {
                        originalRequest.headers.Authorization = newAuthHeader;
                        return apiClient(originalRequest);
                    } else {
                        throw new Error("No token available after refresh");
                    }
                } catch (refreshError) {
                    console.error(
                        "Token refresh failed on 401 retry:",
                        refreshError,
                    );
                    authService.logout();
                    return Promise.reject(error);
                }
            } else {
                // Already tried to refresh, logout user
                console.warn("Token refresh retry failed, logging out user");
                authService.logout();
            }
        }

        // Handle other HTTP errors
        if (error.response?.status === 403) {
            console.error(
                "Forbidden: User does not have permission to access this resource",
            );
        } else if (error.response?.status >= 500) {
            console.error("Server error detected:", error.response.status);
        }

        // Enhanced error logging
        console.error("API Error Details:", {
            status: error.response?.status,
            statusText: error.response?.statusText,
            message: error.message,
            url: error.config?.url,
            method: error.config?.method?.toUpperCase(),
            timestamp: new Date().toISOString(),
            data: error.response?.data,
        });

        return Promise.reject(error);
    },
);

// Enhanced API methods for dashboard data with better error handling
export const dashboardAPI = {
    // Individual Overview endpoints (matching OpenAPI spec)
    getTotalSlips: async (dateRange) => {
        try {
            const params = new URLSearchParams();
            appendDateParams(params, dateRange);
            const queryString = params.toString()
                ? `?${params.toString()}`
                : "";
            return await apiClient.get(`/overview/total-slips${queryString}`);
        } catch (error) {
            console.error("Failed to fetch total slips:", error);
            throw error;
        }
    },

    getTotalValidSlips: async (dateRange) => {
        try {
            const params = new URLSearchParams();
            appendDateParams(params, dateRange);
            const queryString = params.toString()
                ? `?${params.toString()}`
                : "";
            return await apiClient.get(
                `/overview/total-valid-slips${queryString}`,
            );
        } catch (error) {
            console.error("Failed to fetch total valid slips:", error);
            throw error;
        }
    },

    getTotalInvalidSlips: async (dateRange) => {
        try {
            const params = new URLSearchParams();
            appendDateParams(params, dateRange);
            const queryString = params.toString()
                ? `?${params.toString()}`
                : "";
            return await apiClient.get(
                `/overview/total-invalid-slips${queryString}`,
            );
        } catch (error) {
            console.error("Failed to fetch total invalid slips:", error);
            throw error;
        }
    },

    getTotalHillsSales: async (dateRange) => {
        try {
            const params = new URLSearchParams();
            appendDateParams(params, dateRange);
            const queryString = params.toString()
                ? `?${params.toString()}`
                : "";
            return await apiClient.get(
                `/overview/total-hills-sales${queryString}`,
            );
        } catch (error) {
            console.error("Failed to fetch total Hills sales:", error);
            throw error;
        }
    },

    getAverageDiscountValue: async (dateRange) => {
        try {
            const params = new URLSearchParams();
            appendDateParams(params, dateRange);
            const queryString = params.toString()
                ? `?${params.toString()}`
                : "";
            return await apiClient.get(
                `/overview/average-discount-value${queryString}`,
            );
        } catch (error) {
            console.error("Failed to fetch average discount value:", error);
            throw error;
        }
    },

    getUniqueVetStoreCount: async (dateRange) => {
        try {
            const params = new URLSearchParams();
            appendDateParams(params, dateRange);
            const queryString = params.toString()
                ? `?${params.toString()}`
                : "";
            return await apiClient.get(
                `/overview/unique-vet-store-count${queryString}`,
            );
        } catch (error) {
            console.error("Failed to fetch unique vet store count:", error);
            throw error;
        }
    },

    getValidatorAccuracy: async (dateRange) => {
        try {
            const params = new URLSearchParams();
            appendDateParams(params, dateRange);
            const queryString = params.toString()
                ? `?${params.toString()}`
                : "";
            return await apiClient.get(
                `/overview/validator-accuracy${queryString}`,
            );
        } catch (error) {
            console.error("Failed to fetch validator accuracy:", error);
            throw error;
        }
    },

    getOverClaimedTotal: async (dateRange) => {
        try {
            const params = new URLSearchParams();
            appendDateParams(params, dateRange);
            const queryString = params.toString()
                ? `?${params.toString()}`
                : "";
            return await apiClient.get(
                `/overview/over-claimed-total${queryString}`,
            );
        } catch (error) {
            console.error("Failed to fetch over claimed total:", error);
            throw error;
        }
    },

    getUnderPaidTotal: async (dateRange) => {
        try {
            const params = new URLSearchParams();
            appendDateParams(params, dateRange);
            const queryString = params.toString()
                ? `?${params.toString()}`
                : "";
            return await apiClient.get(
                `/overview/under-paid-total${queryString}`,
            );
        } catch (error) {
            console.error("Failed to fetch under paid total:", error);
            throw error;
        }
    },

    // Product endpoints
    getHillsProductPurchaseCounts: async (dateRange) => {
        try {
            const params = new URLSearchParams();
            appendDateParams(params, dateRange);
            const queryString = params.toString()
                ? `?${params.toString()}`
                : "";
            return await apiClient.get(
                `/products/product-purchase-counts${queryString}`,
            );
        } catch (error) {
            console.error(
                "Failed to fetch Hills product purchase counts:",
                error,
            );
            throw error;
        }
    },

    getHillsProductCategorySummary: async (dateRange) => {
        try {
            const params = new URLSearchParams();
            appendDateParams(params, dateRange);
            const queryString = params.toString()
                ? `?${params.toString()}`
                : "";
            return await apiClient.get(
                `/products/product-category-summary${queryString}`,
            );
        } catch (error) {
            console.error(
                "Failed to fetch Hills product category summary:",
                error,
            );
            throw error;
        }
    },

    getBrandBasketShare: async (queryString) => {
        try {
            return await apiClient.get(
                `/products/brand-basket-share${queryString || ""}`,
            );
        } catch (error) {
            console.error("Failed to fetch brand basket share:", error);
            throw error;
        }
    },

    getComplementaryCategories: async (queryString) => {
        try {
            return await apiClient.get(
                `/products/product-complementary-categories${queryString || ""}`,
            );
        } catch (error) {
            console.error("Failed to fetch complementary categories:", error);
            throw error;
        }
    },

    getAvgSpendOnHillsPerSlip: async (dateRange) => {
        try {
            const params = new URLSearchParams();
            appendDateParams(params, dateRange);
            const queryString = params.toString()
                ? `?${params.toString()}`
                : "";
            return await apiClient.get(
                `/products/avg-spend-on-hills-per-slip${queryString}`,
            );
        } catch (error) {
            console.error(
                "Failed to fetch average spend on Hills per slip:",
                error,
            );
            throw error;
        }
    },

    // Additional product endpoints for ProductInsights component
    getProductCategorySummary: async (queryString) => {
        try {
            return await apiClient.get(
                `/products/product-category-summary${queryString || ""}`,
            );
        } catch (error) {
            console.error("Failed to fetch product category summary:", error);
            throw error;
        }
    },

    getProductPurchaseCounts: async (queryString) => {
        try {
            return await apiClient.get(
                `/products/product-purchase-counts?${queryString || ""}`,
            );
        } catch (error) {
            console.error("Failed to fetch product purchase counts:", error);
            throw error;
        }
    },

    // Retailer endpoints
    getRetailerValidationSummary: async (queryString) => {
        try {
            return await apiClient.get(
                `/retailer/retailer-validation-summary${queryString || ""}`,
            );
        } catch (error) {
            console.error(
                "Failed to fetch retailer validation summary:",
                error,
            );
            throw error;
        }
    },

    getTopRetailersByAccuracy: async (queryString) => {
        try {
            return await apiClient.get(
                `/retailer/retailer-validation-accuracy${queryString || ""}`,
            );
        } catch (error) {
            console.error("Failed to fetch top retailers by accuracy:", error);
            throw error;
        }
    },

    // Redemption endpoints
    getCouponRedemptions: async (
        retailer,
        skip = 0,
        take = 10,
        orderBy = "redeemedAt",
        orderDirection = "desc",
        search = "",
        includeDeleted = false,
        dateRange = null,
    ) => {
        try {
            const params = new URLSearchParams();
            params.append("Retailer", retailer);
            params.append("Skip", skip.toString());
            params.append("Take", take.toString());
            if (orderBy) params.append("OrderBy", orderBy);
            if (orderDirection) params.append("OrderDirection", orderDirection);
            if (search) params.append("Search", search);
            if (includeDeleted)
                params.append("IncludeDeleted", includeDeleted.toString());
            appendDateParams(params, dateRange);

            const queryString = `?${params.toString()}`;
            return await apiClient.get(
                `/redemption/coupon-redemptions${queryString}`,
            );
        } catch (error) {
            console.error("Failed to fetch coupon redemptions:", error);
            throw error;
        }
    },

    revalidateCouponRedemption: async (id) => {
        try {
            return await apiClient.patch(
                `/redemption/coupon-redemptions/${encodeURIComponent(id)}/revalidate`,
            );
        } catch (error) {
            console.error("Failed to revalidate coupon redemption:", error);
            throw error;
        }
    },

    // Validator endpoints
    getModeratorValidationAccuracy: async (dateRange) => {
        try {
            // This endpoint now returns ALL data without pagination for better performance
            // Only date filtering is supported - pagination is handled client-side
            const params = new URLSearchParams();
            appendDateParams(params, dateRange);
            const queryString = params.toString()
                ? `?${params.toString()}`
                : "";

            return await apiClient.get(
                `/moderators/validation-accuracy${queryString}`,
                {
                    timeout:
                        API_TIMEOUT_MS === 0
                            ? 0
                            : Math.max(API_TIMEOUT_MS, 300000),
                },
            );
        } catch (error) {
            console.error(
                "Failed to fetch validator validation accuracy:",
                error,
            );
            throw error;
        }
    },

    // moderator: display name (matches coupon_metadata.moderator)
    getModeratorCouponRedemptions: async (
        moderator,
        skip = 0,
        take = 10,
        orderBy = "redeemedAt",
        orderDirection = "desc",
        search = "",
        includeDeleted = false,
        dateRange = null,
    ) => {
        try {
            const params = new URLSearchParams();
            params.append("Moderator", moderator);
            params.append("Skip", skip.toString());
            params.append("Take", take.toString());
            if (orderBy) params.append("OrderBy", orderBy);
            if (orderDirection) params.append("OrderDirection", orderDirection);
            if (search) params.append("Search", search);
            if (includeDeleted)
                params.append("IncludeDeleted", includeDeleted.toString());
            appendDateParams(params, dateRange);

            const queryString = `?${params.toString()}`;
            return await apiClient.get(
                `/moderators/coupon-redemptions${queryString}`,
            );
        } catch (error) {
            console.error(
                "Failed to fetch validator coupon redemptions:",
                error,
            );
            throw error;
        }
    },

    // Co-Pilot specific endpoints
    searchCouponByFilename: async (filename) => {
        try {
            const params = new URLSearchParams();
            params.append("filename", filename);
            const queryString = `?${params.toString()}`;
            return await apiClient.get(
                `/redemption/search-by-filename${queryString}`,
            );
        } catch (error) {
            console.error("Failed to search coupon by filename:", error);
            throw error;
        }
    },

    searchCouponByInvoice: async (invoiceNumber) => {
        try {
            const params = new URLSearchParams();
            params.append("invoice", invoiceNumber);
            const queryString = `?${params.toString()}`;
            return await apiClient.get(
                `/redemption/search-by-invoice${queryString}`,
            );
        } catch (error) {
            console.error("Failed to search coupon by invoice:", error);
            throw error;
        }
    },

    approveCoupon: async (couponCode, approvedAt) => {
        try {
            return await apiClient.post("/redemption/approve", {
                couponCode,
                approvedAt,
            });
        } catch (error) {
            console.error("Failed to approve coupon:", error);
            throw error;
        }
    },

    disputeCoupon: async (couponCode, disputeReason, disputedAt) => {
        try {
            return await apiClient.post("/redemption/dispute", {
                couponCode,
                disputeReason,
                disputedAt,
            });
        } catch (error) {
            console.error("Failed to dispute coupon:", error);
            throw error;
        }
    },
};

export default apiClient;
